import React, { useEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

export const PieChart = ({
  metadata = {},
  data = [],
  showLegend = false,
  legendPosition = "bottom",
  showNumeric = false,
  showPercentage = false,
  innerRadius = 0,
  thresholdValues = [],
}) => {
  const chartRef = useRef(null);
  const rootRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const legendRef = useRef(null);
  const seriesRef = useRef(null);
  const prevDataHashRef = useRef("");

  const styleTooltip = (tooltip) => {
    if (!tooltip) return;
    tooltip.get("background").setAll({
      fill: am5.color(0x333333),
      stroke: am5.color(0x333333),
    });
    tooltip.get("background").adapters.add("fill", () => am5.color(0x333333));

    tooltip.label.setAll({
      fontSize: 10,
      fill: am5.color(0xffffff),
      fontFamily: "Poppins",
    });
    tooltip.label.adapters.add("fill", () => am5.color(0xffffff));
  };

  useEffect(() => {
    if (!chartRef.current) return;

    const root = am5.Root.new(chartRef.current);
    rootRef.current = root;
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        innerRadius: innerRadius > 0 ? am5.percent(innerRadius) : undefined,
        layout:
          legendPosition === "right" || legendPosition === "left"
            ? root.horizontalLayout
            : root.verticalLayout,
      })
    );
    chartInstanceRef.current = chart;

    const valueField = metadata.valueColumn || "orders";
    const categoryField = metadata.analysisColumn || "status";

    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        name: "Series",
        valueField,
        categoryField,
      })
    );
    seriesRef.current = series;

    if (Array.isArray(thresholdValues) && thresholdValues.length > 0) {
      series.slices.template.adapters.add("fill", (fill, target) => {
        const di = target.dataItem;
        if (di) {
          for (const threshold of thresholdValues) {
            if (
              threshold.columnName === categoryField &&
              di.get("category") === threshold.fixedValue &&
              threshold.backgroundColor
            ) {
              return am5.color(threshold.backgroundColor);
            }
          }
        }
        return fill;
      });
      series.slices.template.setAll({ stroke: 0 });
    }

    series.set(
      "tooltip",
      am5.Tooltip.new(root, {
        labelText:
          showNumeric && showPercentage
            ? "{category}: {value} - {percentTotal.formatNumber('0.0')}%"
            : showNumeric
            ? "{category}: {value}"
            : showPercentage
            ? "{category}: {percentTotal.formatNumber('0.0')}%"
            : "{category}",
      })
    );

    const tooltip = series.get("tooltip");
    styleTooltip(tooltip);

    tooltip?.label.adapters.add("text", (text, target) => {
      const di = target.dataItem;
      if (di) {
        const category = di.get("category");
        const value = di.get("value");
        const percent = di.get("valuePercentTotal");
        const valueText = value ?? "";
        const percentText = percent != null ? percent.toFixed(1) + "%" : "";
        if (showNumeric && showPercentage)
          return `${category}: ${valueText} (${percentText})`;
        if (showNumeric) return `${category}: ${valueText}`;
        if (showPercentage) return `${category}: ${percentText}`;
        return `${category}`;
      }
      return text;
    });

    series.labels.template.setAll({ forceHidden: true });
    series.ticks.template.setAll({ forceHidden: true });

    if (showLegend) {
      let leftOrTop = false;
      let legendSettings = {};
      switch (legendPosition) {
        case "right":
          legendSettings = {
            y: am5.percent(50),
            centerY: am5.percent(50),
            layout: root.verticalLayout,
          };
          break;
        case "left":
          legendSettings = {
            y: am5.percent(50),
            centerY: am5.percent(50),
            layout: root.verticalLayout,
          };
          leftOrTop = true;
          break;
        case "top":
          legendSettings = {
            x: am5.percent(50),
            centerX: am5.percent(50),
            layout: root.gridLayout,
          };
          leftOrTop = true;
          break;
        case "bottom":
        default:
          legendSettings = {
            x: am5.percent(50),
            centerX: am5.percent(50),
            layout: root.gridLayout,
          };
          break;
      }

      const legend = leftOrTop
        ? chart.children.unshift(am5.Legend.new(root, legendSettings))
        : chart.children.push(am5.Legend.new(root, legendSettings));
      legendRef.current = legend;

      legend.labels.template.setAll({
        text: `{${categoryField}}`,
        fontSize: 10,
        fontWeight: "400",
        fontFamily: "Poppins, sans-serif",
      });

      legend.valueLabels.template.setAll({
        fontSize: 10,
        fontWeight: "400",
        fontFamily: "Poppins, sans-serif",
      });

      legend.valueLabels.template.adapters.add("text", (text, target) => {
        const di = target.dataItem;
        if (di) {
          const value = di.get("value");
          const percent = di.get("valuePercentTotal");
          const valueText = value ?? "";
          const percentText = percent != null ? percent.toFixed(1) + "%" : "";
          if (showNumeric && showPercentage)
            return `${valueText} - ${percentText}`;
          if (showNumeric) return `${valueText}`;
          if (showPercentage) return `${percentText}`;
        }
        return text;
      });

      legend.data.setAll(series.dataItems);
    }

    series.appear(1000, 100);

    return () => {
      root.dispose();
      rootRef.current = null;
      chartInstanceRef.current = null;
      seriesRef.current = null;
      legendRef.current = null;
    };
  }, []);

  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;

    const newHash = JSON.stringify(data ?? []);
    if (prevDataHashRef.current === newHash) return;
    prevDataHashRef.current = newHash;

    const hiddenByCategory = new Map();
    series.dataItems.forEach((di) => {
      hiddenByCategory.set(di.get("category"), di.get("disabled") === true);
    });

    series.data.setAll(data ?? []);

    series.dataItems.forEach((di) => {
      const cat = di.get("category");
      if (hiddenByCategory.has(cat) && hiddenByCategory.get(cat)) {
        di.set("disabled", true);
      }
    });

    if (legendRef.current) {
      legendRef.current.data.setAll(series.dataItems);
    }
  }, [data]);

  return (
    <div
      ref={chartRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    />
  );
};
